import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiChevronDown, FiMoreHorizontal } from 'react-icons/fi';
import { useFormik } from 'formik';
import { Dropdown } from '@/components/custom/dropdown/dropdown';
import { MedalContainer } from '@/components/custom/medal';
import { PiStudent } from 'react-icons/pi';
import toast from 'react-hot-toast';
import { Badge } from '@/components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { PlayfulCardButton } from '@/components/custom/playful-card';

const STATUS_BADGE_VARIANT = {
  vigente: 'success-soft',
  agendado: 'amber-soft',
  expirado: 'danger-soft'
};

export default function ChallengeRow(props) {
  const { playful = false, handleDeleteChallenge, index, total, challenge, handlePublishChallenge, badgeColor } = props;
  const navigate = useNavigate();

  const challengeForm = useFormik({
    initialValues: {
      published: challenge.published
    }
  });

  const handleChangePublishedChallenge = async () => {
    const response = await handlePublishChallenge(challenge.id, !challengeForm.values.published);
    if (response.error) {
      toast.error('Erro ao publicar desafio');
    } else if (challengeForm.values.published) {
      challengeForm.setFieldValue('published', !challengeForm.values.published);
      toast.success('Desafio despublicado');
    } else {
      challengeForm.setFieldValue('published', !challengeForm.values.published);
      toast.success('Desafio publicado');
    }
  };

  const availabilityBody = useMemo(() => {
    let availability = [];
    if (challenge.classrooms?.length > 0) {
      availability = challenge.classrooms;
    } else if (challenge.courses?.length > 0) {
      availability = challenge.courses;
    } else if (challenge.institutions?.length > 0) {
      availability = challenge.institutions;
    }

    return availability;
  }, [challenge]);

  return (
    <tr className={total === index + 1 ? 'round-dg__row round-dg__row--last' : 'round-dg__row'}>
      <td className="round-dg__cell-header round-dg__cell-header--sm round-dg__cell-header--row">
        <div className="round-dg__user">
          <div className="round-dg__user-inner">
            <h3
              className="round-dg__title"
              title={challenge.title}
            >
              {challenge.title}
            </h3>
          </div>
        </div>
      </td>
      <td className="round-dg__cell round-dg__cell--sm">
        {playful ? (
          <Badge
            variant="info-soft"
            size="sm"
            title={challenge.taxonomy.name}
          >
            {challenge.taxonomy.name}
          </Badge>
        ) : (
          <span
            className="badge badge--tiny badge--primary"
            title={challenge.taxonomy.name}
          >
            {challenge.taxonomy.name}
          </span>
        )}
      </td>
      <td className="round-dg__cell round-dg__cell--sm">
        <span
          className="u-text-truncate"
          title={challenge.user.name}
        >
          {challenge.user.name}
        </span>
      </td>
      <td className="round-dg__cell round-dg__cell--sm">
        {challenge['initial-date'].toLocaleDateString('pt-br', { day: 'numeric' })} a {challenge['end-date'].toLocaleDateString('pt-br')}
      </td>
      <td className="round-dg__cell round-dg__cell--sm">
        <div
          className="card__badges card__badges--one-line mb-0"
          style={{ lineHeight: 1, alignItems: 'center' }}
        >
          {availabilityBody.length > 1 ? (
            <>
              <span
                className="u-text-truncate"
                title={availabilityBody[0].name ?? availabilityBody[0].title}
              >
                {availabilityBody[0].name ?? availabilityBody[0].title}
              </span>
              <span
                className="badge badge--tiny badge--more"
                title={availabilityBody?.slice(1).map(item => item.name ?? item.title)}
              >
                +{availabilityBody?.slice(1).length}
              </span>
            </>
          ) : availabilityBody.length === 1 ? (
            <span
              className="u-text-truncate"
              title={availabilityBody[0].name ?? availabilityBody[0].title}
            >
              {availabilityBody[0].name ?? availabilityBody[0].title}
            </span>
          ) : (
            <span
              className="u-text-truncate"
              title={'Público'}
            >
              Público
            </span>
          )}
        </div>
      </td>
      <td className="round-dg__cell round-dg__cell--sm">
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <MedalContainer value={<PiStudent />} />
          <span
            className="u-text-truncate"
            title={`${challenge?.['participation-count'] ?? '-'} (${challenge?.['participants-percentage'] ?? '-'}%)`}
          >
            {challenge?.['participation-count'] ?? '-'} ({challenge?.['participants-percentage'] ?? '-'}%)
          </span>
        </span>
      </td>
      {playful && (
        <td className="round-dg__cell round-dg__cell--sm">
          <Badge
            variant={challengeForm.values.published ? 'success-soft' : 'muted'}
            size="sm"
            title={challengeForm.values.published ? 'Publicado' : 'Despublicado'}
          >
            {challengeForm.values.published ? 'Publicado' : 'Despublicado'}
          </Badge>
        </td>
      )}
      <td className="round-dg__cell round-dg__cell--sm">
        {playful ? (
          <Badge
            variant={STATUS_BADGE_VARIANT[challenge.status] ?? 'muted'}
            size="sm"
            title={challenge.status?.charAt(0).toUpperCase() + challenge.status?.slice(1)}
          >
            {challenge.status?.charAt(0).toUpperCase() + challenge.status?.slice(1)}
          </Badge>
        ) : (
          <span
            className={`badge badge--tiny badge--${badgeColor(challenge)}`}
            title={challenge.status?.charAt(0).toUpperCase() + challenge.status?.slice(1)}
          >
            {challenge.status?.charAt(0).toUpperCase() + challenge.status?.slice(1)}
          </span>
        )}
      </td>
      {!playful && (
        <td className="round-dg__cell round-dg__cell--sm">
          <div className="form__check form__switch !mb-0">
            <input
              className="form__check-input"
              id={`published-${index}`}
              name={`published-${index}`}
              type="checkbox"
              defaultChecked={challengeForm.values.published}
              onClick={handleChangePublishedChallenge}
            />
            <label htmlFor={`published-${index}`}>Publicar</label>
          </div>
        </td>
      )}
      {playful ? (
        <td
          className="round-dg__cell"
          style={{ width: 76, paddingLeft: 8, paddingRight: 20 }}
        >
          <div className="flex justify-center">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <PlayfulCardButton
                  variant="outline"
                  aria-label="Opções"
                  className="h-9 w-9 shrink-0 gap-0 p-0"
                >
                  <FiChevronDown size={20} />
                </PlayfulCardButton>
              </DropdownMenuTrigger>

              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={handleChangePublishedChallenge}>{challengeForm.values.published ? 'Despublicar' : 'Publicar'}</DropdownMenuItem>
                <DropdownMenuItem onSelect={() => navigate(`/desafio-da-semana/editar/${challenge.id}`)}>Editar</DropdownMenuItem>
                <DropdownMenuItem onSelect={() => handleDeleteChallenge(challenge.id)}>Excluir</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </td>
      ) : (
        <td className="round-dg__cell round-dg__cell--sm">
          <Dropdown>
            <Dropdown.Toggle>
              <FiMoreHorizontal />
            </Dropdown.Toggle>

            <Dropdown.Content>
              <Dropdown.Item to={`/desafio-da-semana/editar/${challenge.id}`}>Editar</Dropdown.Item>
              <Dropdown.Item onClick={() => handleDeleteChallenge(challenge.id)}>Excluir</Dropdown.Item>
              {/* TODO: <Dropdown.Item>Ver resultados</Dropdown.Item> */}
            </Dropdown.Content>
          </Dropdown>
        </td>
      )}
    </tr>
  );
}
